import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { forgotPasswordSchema, type ForgotPasswordFormValues } from '@emazy/shared-validation';

import { buildLoginRoute, buildResetPasswordRoute } from '@/config/routes';
import { FormErrorSummary, SubmitButton } from '@/features/auth/components';
import { useAuth } from '@/features/auth/context';
import { getAuthErrorMessage } from '@/features/auth/utils';
import { useDocumentMetadata } from '@/hooks/useDocumentMetadata';
import { env } from '@/config/env';

export function ForgotPasswordPage() {
  useDocumentMetadata('Forgot Password');
  const [searchParams] = useSearchParams();
  const { forgotPassword } = useAuth();
  const [submittedEmail, setSubmittedEmail] = useState<string>();
  const [error, setError] = useState<string>();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: searchParams.get('email') ?? '' },
  });

  async function submit(values: ForgotPasswordFormValues) {
    setError(undefined);
    try {
      await forgotPassword(values);
      setSubmittedEmail(values.email);
    } catch (caught) {
      setError(getAuthErrorMessage(caught));
    }
  }

  if (submittedEmail)
    return (
      <div className="auth-page auth-success">
        <i className="pi pi-envelope" aria-hidden="true" />
        <p className="eyebrow">Check the next step</p>
        <h1>Reset instructions prepared</h1>
        <p>If an account exists for that email, password-reset instructions have been prepared.</p>
        {import.meta.env.DEV && env.authMode === 'mock' && (
          <p className="mock-code-note">
            Development only: use mock reset code <strong>123456</strong>.
          </p>
        )}
        <Link
          className="button-link button-link--primary"
          to={buildResetPasswordRoute({ email: submittedEmail })}
        >
          Enter reset code
        </Link>
        <Link className="text-link" to={buildLoginRoute({ email: submittedEmail })}>
          Return to sign in
        </Link>
      </div>
    );

  return (
    <div className="auth-page">
      <p className="eyebrow">Account recovery</p>
      <h1>Forgot your password?</h1>
      <p className="auth-page__intro">
        Enter your email. The response stays the same whether or not an account exists.
      </p>
      <FormErrorSummary message={error} />
      <form className="auth-form" noValidate onSubmit={handleSubmit(submit)}>
        <label className="auth-field" htmlFor="forgot-email">
          <span>Email</span>
          <input
            id="forgot-email"
            className="p-inputtext p-component"
            type="email"
            autoComplete="email"
            {...register('email')}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'forgot-email-error' : undefined}
          />
          {errors.email && (
            <small id="forgot-email-error" role="alert">
              {errors.email.message}
            </small>
          )}
        </label>
        <SubmitButton label="Prepare reset instructions" loading={isSubmitting} />
      </form>
      <p className="auth-page__alternate">
        <Link to={buildLoginRoute({ email: searchParams.get('email') ?? undefined })}>
          Return to sign in
        </Link>
      </p>
    </div>
  );
}
